import { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Card, CardContent } from '../components/ui/card';
import {
  TUTORIALS,
  TUTORIAL_CATEGORIES,
  getTutorialById,
} from '../data/tutorials';
import {
  GraduationCap, Search, ArrowRight, Lightbulb, AlertTriangle,
  CheckCircle2, ExternalLink, ChevronLeft,
} from 'lucide-react';

const SectionRenderer = ({ section }) => {
  if (section.type === 'paragraph') {
    return <p className="text-zinc-700 leading-relaxed">{section.text}</p>;
  }
  if (section.type === 'heading') {
    return (
      <h3 className="text-base font-semibold text-zinc-900 mt-2">
        {section.text}
      </h3>
    );
  }
  if (section.type === 'list') {
    return (
      <ul className="space-y-1.5">
        {section.items.map((item, i) => (
          <li key={i} className="flex gap-2 text-zinc-700">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" />
            <span className="leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (section.type === 'steps') {
    return (
      <ol className="space-y-2.5">
        {section.items.map((item, i) => (
          <li key={i} className="flex gap-3 text-zinc-700">
            <span className="shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold flex items-center justify-center mt-0.5">
              {i + 1}
            </span>
            <span className="leading-relaxed">{item}</span>
          </li>
        ))}
      </ol>
    );
  }
  if (section.type === 'tip') {
    return (
      <div className="flex gap-3 rounded-lg border border-indigo-100 bg-indigo-50/60 p-3">
        <Lightbulb className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5" />
        <p className="text-sm text-indigo-900 leading-relaxed">{section.text}</p>
      </div>
    );
  }
  if (section.type === 'warning') {
    return (
      <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50/80 p-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900 leading-relaxed">{section.text}</p>
      </div>
    );
  }
  return null;
};

export const TutorialsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Read ?id= from the URL so deep links (e.g. from the Dashboard onboarding
  // checklist or inline Help links) open the right tutorial.
  const urlParams = new URLSearchParams(location.search);
  const urlId = urlParams.get('id');

  const [activeId, setActiveId] = useState(urlId || TUTORIALS[0].id);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (urlId && urlId !== activeId) setActiveId(urlId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return TUTORIALS;
    return TUTORIALS.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        t.summary.toLowerCase().includes(q)
    );
  }, [query]);

  const active = getTutorialById(activeId) || TUTORIALS[0];

  const handleSelect = (id) => {
    setActiveId(id);
    navigate(`/tutorials?id=${id}`, { replace: true });
  };

  return (
    <div className="space-y-6" data-testid="tutorials-page">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-600 text-white flex items-center justify-center">
            <GraduationCap className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">
              Tutorials
            </h1>
            <p className="text-zinc-500 mt-0.5 text-sm">
              Short, practical guides for every feature. Start anywhere.
            </p>
          </div>
        </div>
        <div className="relative sm:w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tutorials…"
            className="pl-9"
            data-testid="tutorials-search"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Category + tutorial list */}
        <aside className="bg-white rounded-xl border border-zinc-200">
          <ScrollArea className="h-[calc(100vh-220px)] lg:h-[calc(100vh-200px)]">
            <div className="p-3 space-y-5">
              {TUTORIAL_CATEGORIES.map((cat) => {
                const catTutorials = filtered.filter((t) => t.category === cat.id);
                if (catTutorials.length === 0) return null;
                return (
                  <div key={cat.id}>
                    <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 px-2 mb-2">
                      {cat.label}
                    </p>
                    <div className="space-y-0.5">
                      {catTutorials.map((t) => {
                        const Icon = t.icon;
                        const isActive = t.id === activeId;
                        return (
                          <button
                            key={t.id}
                            onClick={() => handleSelect(t.id)}
                            data-testid={`tutorial-link-${t.id}`}
                            className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-md text-sm text-left transition-colors ${
                              isActive
                                ? 'bg-indigo-50 text-indigo-700 font-medium'
                                : 'text-zinc-700 hover:bg-zinc-50'
                            }`}
                          >
                            <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-indigo-600' : 'text-zinc-400'}`} />
                            <span className="truncate">{t.title}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <p className="text-sm text-zinc-500 text-center py-8">
                  No tutorials match "{query}".
                </p>
              )}
            </div>
          </ScrollArea>
        </aside>

        {/* Active tutorial */}
        <div>
          {/* Mobile back button when a tutorial is open */}
          <div className="lg:hidden mb-3">
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900"
            >
              <ChevronLeft className="w-4 h-4" /> Back to dashboard
            </Link>
          </div>
          <Card className="border-zinc-200">
            <CardContent className="p-6 sm:p-8 space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                  <active.icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2
                    className="text-xl sm:text-2xl font-bold text-zinc-900 tracking-tight"
                    data-testid="tutorial-title"
                  >
                    {active.title}
                  </h2>
                  <p className="text-zinc-600 mt-1 leading-relaxed">
                    {active.summary}
                  </p>
                </div>
              </div>

              <div className="space-y-4 pt-2">
                {active.sections.map((section, i) => (
                  <SectionRenderer key={i} section={section} />
                ))}
              </div>

              {active.tryLink && (
                <div className="pt-4 flex flex-wrap items-center gap-3 border-t border-zinc-100">
                  <Link to={active.tryLink.href}>
                    <Button
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid={`tutorial-try-${active.id}`}
                    >
                      {active.tryLink.label}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <span className="inline-flex items-center gap-1.5 text-xs text-zinc-500">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                    Opens in the same tab — come back any time.
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          <p className="mt-4 text-xs text-zinc-500 flex items-center gap-1.5">
            <ExternalLink className="w-3 h-3" />
            Need something that isn't covered here? Email support from the Settings page.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TutorialsPage;
